import { minorDigits } from '@/ops/format';
import type { AccountSubscription } from '@/api/types';

/**
 * Shared subscription presentation: money formatting, status colouring, and the badge.
 *
 * One module because the Accounts table, the account header and the Billing tab all render the same
 * facts, and three copies of "how do we show past_due" is how they end up disagreeing about which
 * states are alarming.
 */

/**
 * Formats integer minor units as money.
 *
 * Minor units are the wire format everywhere in this feature precisely so that this — dividing by 100
 * for display — is the ONLY place a fractional value exists. Returns '—' for null, never '$0.00': a
 * price nobody has recorded and a price of zero are different facts.
 */
export function formatMoney(minor: number | null | undefined, currency: string | null): string {
  if (minor === null || minor === undefined || !Number.isFinite(minor)) return '—';

  // The currency's OWN minor-unit digits (KWD 3, JPY 0) — dividing by 100 was off by ten or a hundred for those.
  const digits = minorDigits(currency);
  const major = minor / 10 ** digits;
  if (!currency) return major.toLocaleString('en-US', { maximumFractionDigits: digits });
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      // Whole amounts read better without ".00" in a dense table; fractional ones need every place.
      minimumFractionDigits: Number.isInteger(major) ? 0 : digits,
      maximumFractionDigits: digits,
    }).format(major);
  } catch {
    // An unrecognised ISO code must not blank the cell. Show the number and the raw code.
    return `${major.toLocaleString('en-US')} ${currency}`.trim();
  }
}

/** 'past_due' → 'Past due'. IAM's snake_case statuses are not display strings. */
export function humanizeStatus(status: string | null): string {
  if (!status) return 'Not synced';
  const text = status.replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Maps a status onto a semantic colour.
 *
 * `past_due` is WARNING, not danger, and that is deliberate: it changes nothing about the tenant's
 * access upstream (see docs/subscriptions.md), so colouring it red would tell the reader an outage is
 * happening when the truth is "chase an invoice". Only suspended and churned — the two states that
 * actually cut access — are danger.
 */
export function statusTone(status: string | null): 'success' | 'warning' | 'danger' | 'muted' {
  switch (status) {
    case 'active':
      return 'success';
    case 'trialing':
    case 'past_due':
    case 'cancelled':
      return 'warning';
    case 'suspended':
    case 'churned':
      return 'danger';
    default:
      return 'muted';
  }
}

const TONE_COLOR: Record<string, string> = {
  success: 'var(--success)',
  warning: 'var(--warning)',
  danger: 'var(--error)',
  muted: 'var(--text-subtle)',
};

/**
 * The status pill.
 *
 * A tenant with nothing replicated gets "Not synced" rather than a blank or a plan name, because that
 * state is genuinely actionable: IAM's feature gate fails OPEN for a tenant with no subscription row,
 * so those tenants are running unenforced.
 */
export function StatusBadge({ subscription }: { subscription: AccountSubscription }) {
  if (!subscription.is_replicated) {
    return (
      <span
        className="badge-muted"
        title="No subscription replicated for this tenant. IAM's feature gate fails OPEN for it — run IAM's subscription backfill."
      >
        not synced
      </span>
    );
  }

  const tone = statusTone(subscription.status);
  return (
    <span
      className="t-label"
      style={{
        color: TONE_COLOR[tone],
        border: `1px solid ${TONE_COLOR[tone]}`,
        borderRadius: 999,
        padding: '1px 8px',
        whiteSpace: 'nowrap',
      }}
    >
      {humanizeStatus(subscription.status)}
    </span>
  );
}

/** "12 / 10" with the overage coloured. "12 / ∞" when the limit is unenforced. */
export function SeatsCell({
  seatsUsed,
  seatLimit,
  isAtLimit,
}: {
  seatsUsed: number | null;
  seatLimit: number | null;
  isAtLimit: boolean;
}) {
  // '?' not 0 — a count that could not be read and an empty account are different facts.
  const used = seatsUsed === null ? '?' : seatsUsed.toLocaleString('en-US');
  // '∞' not '0' and not '—': the limit exists and is deliberately unenforced.
  const limit = seatLimit === null ? '∞' : seatLimit.toLocaleString('en-US');

  return (
    <span style={{ color: isAtLimit ? 'var(--warning)' : undefined, whiteSpace: 'nowrap' }}>
      {used} / {limit}
    </span>
  );
}

/**
 * "in 5d" / "3d ago" / "today".
 *
 * A lapsed trial reads as elapsed time rather than a negative number, because "-9d" makes the reader
 * do arithmetic to learn the one thing that matters: it is already over.
 */
export function formatTrialCountdown(daysRemaining: number | null): string | null {
  if (daysRemaining === null) return null;
  if (daysRemaining === 0) return 'today';
  return daysRemaining > 0 ? `in ${daysRemaining}d` : `${Math.abs(daysRemaining)}d ago`;
}
